"use client";

import { Button } from "@aio/platform-ui";
import * as React from "react";

export interface FormPageProps {
  /**
   * Page title. Accepts a plain string OR a `ReactNode` so callers can pass
   * an i18n-resolving wrapper (e.g. `<TranslatedText id="..." />`) without
   * forcing the page-factory to do server-side translation lookups.
   */
  title: React.ReactNode;
  description?: React.ReactNode;
  /** The form body (Plan UI-4 will provide a real engine) */
  children: React.ReactNode;
  /** Right-rail content (history/comments/attachments) */
  sidePanel?: React.ReactNode;
  /** Whether the form has unsaved changes — affects sticky bar appearance */
  dirty?: boolean;
  onSave?: () => void;
  onSaveAndClose?: () => void;
  onCancel?: () => void;
  saving?: boolean;
}

export function FormPage({
  title,
  description,
  children,
  sidePanel,
  dirty,
  onSave,
  onSaveAndClose,
  onCancel,
  saving,
}: FormPageProps) {
  return (
    <div className="pb-20">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
      </header>
      <div className={sidePanel ? "grid gap-6 lg:grid-cols-[1fr,20rem]" : ""}>
        <div>{children}</div>
        {sidePanel ? <aside className="space-y-4">{sidePanel}</aside> : null}
      </div>
      {onSave || onSaveAndClose || onCancel ? (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-bg-elevated/95 px-4 py-3 backdrop-blur md:px-8"
          role="toolbar"
          aria-label="Form actions"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <span className="text-sm text-fg-muted">
              {dirty ? "Unsaved changes" : "All changes saved"}
            </span>
            <div className="flex items-center gap-2">
              {onCancel ? (
                <Button variant="ghost" onClick={onCancel} disabled={saving}>
                  Cancel
                </Button>
              ) : null}
              {onSaveAndClose ? (
                <Button variant="outline" onClick={onSaveAndClose} disabled={saving}>
                  Save and close
                </Button>
              ) : null}
              {onSave ? (
                <Button onClick={onSave} {...(saving ? { loading: true } : {})}>
                  Save
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
